import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import ViewListIcon from '@mui/icons-material/ViewList';
import {
  Avatar,
  AvatarGroup,
  Badge,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import type {
  Assessment,
  Evaluation,
  EvaluatorAssignment,
  Rubric,
  Supplier,
} from '../../types/procurement';
import type { StagedSupplier } from '../../utils/fileUploadUtils';
import BulkSubmissionStaging from './Submissions/BulkSubmissionStaging';
import SupplierProgressBoard from './SupplierProgressBoard';

interface Props {
  suppliers: Supplier[];
  rubric: Rubric | null;
  evaluations: Evaluation[]; // Full evaluations
  assessments: Assessment[];
  evaluatorAssignments: EvaluatorAssignment[];
  isPanelChair: boolean;
  isUploading: boolean;
  onUploadAll: (stagedSuppliers: StagedSupplier[]) => Promise<void>;
  onUpdateStatus: (supplierId: string, newStatus: Supplier['status']) => void;
  onAddEvaluatorClick: (supplierId: string) => void;
  onOpenEvaluation: (supplierId: string) => void;
  onShowSnackbar?: (message: string, severity: 'success' | 'error') => void;
}

const STATUS_ORDER = ['SUBMITTED', 'EVALUATING', 'REJECTED', 'SHORTLISTED', 'AWARDED'];

const stringToColor = (string: string) => {
  let hash = 0;
  let i = 0;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
};

const SupplierRow = ({
  supplier,
  assessments,
  evaluatorAssignments,
  evaluations,
  isPanelChair,
  onAddEvaluatorClick,
  onUpdateStatus,
  onOpenEvaluation,
}: {
  supplier: Supplier;
  assessments: Assessment[];
  evaluatorAssignments: EvaluatorAssignment[];
  evaluations: Evaluation[];
  isPanelChair: boolean;
  onAddEvaluatorClick: () => void;
  onUpdateStatus: (newStatus: Supplier['status']) => void;
  onOpenEvaluation: () => void;
}) => {
  const { account } = useAccount();

  const supplierAssessments = assessments.filter((a) => a.supplierId === supplier.id);
  const compAssessment = supplierAssessments.find((a) => a.type === 'COMPLIANCE_CHECK');
  const nliAssessment = supplierAssessments.find((a) => a.type === 'NLI_SIMULATION');

  let complianceStatus = 'Pending';
  let complianceColor: 'default' | 'success' | 'error' | 'warning' = 'default';

  if (compAssessment?.results) {
    const allMet = compAssessment.results.every((r) => r.met);
    complianceStatus = allMet ? 'Pass' : 'Fail';
    complianceColor = allMet ? 'success' : 'error';
  }

  const nliWarnings = nliAssessment?.contradictions?.length || 0;

  const assignedEvaluators = evaluatorAssignments.filter((a) => a.supplierId === supplier.id);
  const isAssigned = assignedEvaluators.some((a) => a.userId === account?.uid);

  return (
    <TableRow
      sx={{
        '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
        '&:last-child td, &:last-child th': { border: 0 },
      }}
    >
      <TableCell component="th" scope="row">
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {supplier.name}
        </Typography>
        {supplier.submissionDate && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Submitted: {new Date(supplier.submissionDate).toLocaleDateString()}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {supplier.documents.length} Docs
        </Typography>
      </TableCell>
      <TableCell>
        {isPanelChair ? (
          <FormControl size="small" variant="outlined">
            <Select
              value={supplier.status}
              onChange={(e) => onUpdateStatus(e.target.value as Supplier['status'])}
              sx={{
                minWidth: 130,
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '& .MuiSelect-select': { p: 0 },
              }}
              renderValue={(selected) => (
                <Chip
                  label={selected.replace('_', ' ')}
                  size="small"
                  color={
                    selected === 'AWARDED'
                      ? 'success'
                      : selected === 'REJECTED'
                        ? 'error'
                        : selected === 'SHORTLISTED'
                          ? 'info'
                          : selected === 'EVALUATING'
                            ? 'warning'
                            : 'default'
                  }
                  variant="filled"
                  sx={{ cursor: 'pointer', borderRadius: 1 }}
                />
              )}
            >
              {STATUS_ORDER.map((status) => (
                <MenuItem key={status} value={status}>
                  {status.replace('_', ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Chip
            label={supplier.status.replace('_', ' ')}
            size="small"
            color={
              supplier.status === 'AWARDED'
                ? 'success'
                : supplier.status === 'REJECTED'
                  ? 'error'
                  : supplier.status === 'SHORTLISTED'
                    ? 'info'
                    : supplier.status === 'EVALUATING'
                      ? 'warning'
                      : 'default'
            }
            variant="filled"
          />
        )}
      </TableCell>
      <TableCell>
        <Chip
          label={complianceStatus}
          color={complianceColor}
          size="small"
          variant="filled"
          sx={{ minWidth: 60 }}
        />
      </TableCell>
      <TableCell>
        <Chip
          label={`${nliWarnings} Flags`}
          color={nliWarnings === 0 ? 'success' : 'warning'}
          size="small"
          variant="filled"
        />
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AvatarGroup
            max={4}
            sx={{ '& .MuiAvatar-root': { width: 28, height: 28, fontSize: '0.75rem' } }}
          >
            {assignedEvaluators.map((ea, i) => {
              const userEval = evaluations.find(
                (e) => e.supplierId === supplier.id && e.evaluatorId === ea.userId
              );
              const isSubmitted = userEval?.status === 'SUBMITTED';
              const avatar = (
                <Avatar
                  sx={{
                    bgcolor: isSubmitted
                      ? (theme) => theme.palette.grey[400]
                      : stringToColor(ea.userName || 'User'),
                    color: isSubmitted ? 'common.white' : undefined,
                  }}
                >
                  {ea.userName?.charAt(0) || 'U'}
                </Avatar>
              );

              return (
                <Tooltip
                  key={ea.userId || i}
                  title={`${ea.userName || 'User'} ${isSubmitted ? '(Submitted)' : '(Evaluating)'}`}
                >
                  {isSubmitted ? (
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      sx={{
                        '& .MuiBadge-badge': { right: '50%', transform: 'translate(50%, 40%)' },
                      }}
                      badgeContent={
                        <CheckCircleOutlineIcon
                          sx={{
                            fontSize: 16,
                            color: 'success.main',
                            bgcolor: 'background.paper',
                            borderRadius: '50%',
                          }}
                        />
                      }
                    >
                      {avatar}
                    </Badge>
                  ) : (
                    avatar
                  )}
                </Tooltip>
              );
            })}
          </AvatarGroup>
          {isPanelChair && (
            <Tooltip title="Add Evaluator">
              <IconButton size="small" onClick={onAddEvaluatorClick}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell align="right">
        <Tooltip
          title={!isAssigned ? 'You must be assigned to evaluate this supplier' : 'Open Evaluation'}
        >
          <span>
            <Button
              variant="contained"
              size="small"
              onClick={onOpenEvaluation}
              endIcon={<OpenInNewIcon fontSize="small" />}
              sx={{ mr: 1, textTransform: 'none' }}
              disabled={!isAssigned}
            >
              Evaluate
            </Button>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

const SupplierConsolidatedDashboard: React.FC<Props> = ({
  suppliers,
  assessments,
  evaluatorAssignments,
  evaluations,
  isPanelChair,
  isUploading,
  onUploadAll,
  onUpdateStatus,
  onAddEvaluatorClick,
  onOpenEvaluation,
  onShowSnackbar,
}) => {
  const [viewMode, setViewMode] = useState<'table' | 'kanban'>('table');

  const renderToggle = () => (
    <ToggleButtonGroup
      value={viewMode}
      exclusive
      onChange={(_, newMode) => {
        if (newMode) {
          setViewMode(newMode);
        }
      }}
      size="small"
    >
      <ToggleButton value="table">
        <ViewListIcon sx={{ mr: 1 }} fontSize="small" /> Table
      </ToggleButton>
      <ToggleButton value="kanban">
        <ViewKanbanIcon sx={{ mr: 1 }} fontSize="small" /> Kanban
      </ToggleButton>
    </ToggleButtonGroup>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {isPanelChair ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Upload Supplier Submissions
            </Typography>
            <Box component="ul" sx={{ mt: 0, pl: 2, color: 'text.secondary', '& li': { mb: 0.5 } }}>
              <Typography component="li" variant="body2">
                Drag and drop files or ZIPs below.
              </Typography>
              <Typography component="li" variant="body2">
                The system auto-extracts documents and matches them to suppliers based on folder/ZIP
                name.
              </Typography>
            </Box>
          </Box>
          <BulkSubmissionStaging onUploadAll={onUploadAll} isUploading={isUploading} />
          <Box sx={{ mt: 2 }}>{renderToggle()}</Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: -2 }}>{renderToggle()}</Box>
      )}

      {viewMode === 'table' ? (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead
              sx={{
                bgcolor: (theme) => theme.palette.greyContrast,
                '& .MuiTableCell-root': {
                  color: (theme) => theme.palette.getContrastText(theme.palette.greyContrast),
                },
              }}
            >
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Supplier</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Compliance</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Triage Risk</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Evaluators</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...suppliers]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((supplier) => (
                  <SupplierRow
                    key={supplier.id}
                    supplier={supplier}
                    assessments={assessments}
                    evaluatorAssignments={evaluatorAssignments}
                    evaluations={evaluations}
                    isPanelChair={isPanelChair}
                    onAddEvaluatorClick={() => onAddEvaluatorClick(supplier.id)}
                    onUpdateStatus={(newStatus) => onUpdateStatus(supplier.id, newStatus)}
                    onOpenEvaluation={() => onOpenEvaluation(supplier.id)}
                  />
                ))}
              {suppliers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography variant="body1" color="text.secondary">
                      No suppliers submitted yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <SupplierProgressBoard
          suppliers={suppliers}
          onUpdateStatus={onUpdateStatus}
          onSendEmail={(supplier, type) => {
            if (onShowSnackbar) {
              onShowSnackbar(
                `Email notification (${type}) triggered for ${supplier.name}`,
                'success'
              );
            }
          }}
        />
      )}
    </Box>
  );
};

export default SupplierConsolidatedDashboard;
